import classNames from 'classnames';
import React, { Fragment, useEffect, useState } from 'react';
import SemanticHeader from '../Chapter/semantic-header';
import CollapsibleText from '../CollapsibleText';
import useMatomoTrackEventOnChange from '../Hooks/useMatomoTrackEventOnChange';
import useWindowLocationHref from '../Hooks/useWindowLocation';

const ImageGallery = (props) => {
  const href = useWindowLocationHref();
  const numOfImages = props.NumOfImages;

  const [currentImage, setCurrentImage] = useState(1);

  useEffect(() => {
    if (containsImages(props.Images)) {
      trackEvent();
    }
  }, []);
  const trackEvent = () => {};

  const getNextImageNum = () => {
    return currentImage + 1 > numOfImages ? 1 : currentImage + 1;
  };

  const getPrevImageNum = () => {
    return currentImage - 1 < 1 ? numOfImages : currentImage - 1;
  };

  const nextImage = () => {
    setCurrentImage(getNextImageNum());
    trackEvent();
  };

  const prevImage = () => {
    setCurrentImage(getPrevImageNum());
    trackEvent();
  };

  const containsImages = (imageBlock) => {
    let hasImage = false;
    try {
      imageBlock.props.blocks.forEach((element) => {
        if (element.ImageUrl) {
          hasImage = true;
        }
      });
    } catch (error) {
      throw new Error('Du måste ha minst en bild i bildspelet');
    }

    return hasImage;
  };

  useMatomoTrackEventOnChange(
    'Bildspel',
    'Bläddrar',
    href || 'novalue',
    currentImage
  );

  const { Heading, Images, Dir, Translations } = props;

  const hasImages = containsImages(Images);

  const swipeDir = Dir === 'rtl' ? 1 : -1;

  const dataPrintSidebarTitle =
    Heading ||
    props.CurrentPageProps?.Content?.Translations?.imagegalleryblock ||
    '';

  if (!Translations)
    throw new Error(`There is no Translation object for ImageGallery`);
  return (
    <div
      className="c-image-gallery"
      data-print-sidebar-title={dataPrintSidebarTitle}
      data-type-media-slider={'MediaSlider'}
      data-is-member-of-chapter-container={
        props.isMemberOfChapterContainer || 'false'
      }
    >
      {Heading && (
        <SemanticHeader
          isH3={props.isH3}
          className={classNames({
            'c-image-gallery__heading': true,
          })}
        >
          {Heading}
        </SemanticHeader>
      )}
      <div className="c-image-gallery__wrapper">
        <div className="c-image-gallery__content">
          <div className="c-image-gallery__items-container">
            <div
              className="c-image-gallery__items"
              aria-live="polite"
              style={{
                transform: `translateX(${
                  (currentImage - 1) * 100 * swipeDir
                }%)`,
              }}
            >
              {React.cloneElement(Images, {
                currentImage: currentImage,
                ParentBlockId: props.UniqueBlockId,
                Translations: Translations,
                numOfImages: props.NumOfImages,
                hasImages,
                NonCollapsible: props.NonCollapsible,
                IllustratorTop: props.IllustratorTop,
                ShowAuthor: props.ShowAuthor ?? true,
              })}
            </div>
          </div>
          <div
            className={classNames({
              'c-image-gallery__ctrls': true,
              'c-image-gallery__ctrls--no-images': !hasImages,
            })}
          >
            <button
              type="button"
              className="c-image-gallery__nextprev c-image-gallery__nextprev--next"
              onClick={() => nextImage()}
            >
              <span className="show-for-sr">{Translations.next}</span>
            </button>
            <button
              type="button"
              className="c-image-gallery__nextprev c-image-gallery__nextprev--prev"
              onClick={() => prevImage()}
            >
              <span className="show-for-sr">{Translations.previous}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Illustrator = (props) =>
  props.ShowAuthor && (props.Illustrator || props.Photographer) ? (
    <div className="c-image-gallery__item__illustrator">
      {props.Illustrator
        ? `${props.Translations.illustrator}: ${props.Illustrator}`
        : `${props.CurrentPageProps.Content.Translations.photographer}: ${props.Photographer}`}
    </div>
  ) : null;

const ImageGalleryImage = (props) => {
  const imageClasses = classNames({
    'c-image-gallery__item': true,
    'c-image-gallery__item--current': props.index + 1 === props.currentImage,
    'c-image-gallery__item--no-images': !props.hasImages,
  });

  return (
    <div
      className={imageClasses}
      aria-hidden={props.index + 1 !== props.currentImage}
    >
      <span className="show-for-sr">
        {props.Translations.counter} {props.currentImage}{' '}
      </span>

      <div className="c-image-gallery__item__image">
        {props.hasImages && props.Image}
        <div className="c-image-gallery__current-container">
          <div className="c-image-gallery__current">
            <span>{props.Translations.counter}</span>{' '}
            <span>
              {props.currentImage}/{props.numOfImages}
            </span>
          </div>
        </div>
      </div>

      {props.IllustratorTop && <Illustrator {...props} />}

      {props.ImageGalleryHeading && (
        <h3 className="c-image-gallery__item__heading">
          {props.ImageGalleryHeading}
        </h3>
      )}

      {(props.ImageGalleryText || props.ImageGalleryLinks) && (
        <div className="c-image-gallery__item__text-container">
          {props.NonCollapsible && (
            <div className="c-image-gallery__item__text-wrapper">
              <ImageGalleryText {...props} />
            </div>
          )}
          {!props.NonCollapsible && (
            <CollapsibleText
              buttonReadMoreText={props.ReadMoreText}
              buttonReadLessText={props.ReadLessText}
              Modifiers={{ 'c-collapsible-text--image-gallery': true }}
            >
              <ImageGalleryText {...props} />
            </CollapsibleText>
          )}
        </div>
      )}

      {!props.IllustratorTop && <Illustrator {...props} />}
    </div>
  );
};

const ImageGalleryText = (props) => {
  return (
    <Fragment>
      {props.ImageGalleryText && (
        <div className="c-image-gallery__item__text">
          {props.ImageGalleryText}
        </div>
      )}
      {props.ImageGalleryLinks && (
        <div className="c-image-gallery__item__linklist">
          {props.ImageGalleryLinks}
        </div>
      )}
    </Fragment>
  );
};

export { ImageGallery, ImageGalleryImage, ImageGalleryText };
